import { useEffect, useId, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useT } from '../../i18n';
import { fetchMe, type MeResult } from '../../lib/api/me';
import {
  fetchScenarioCatalog,
  startScenario,
  type DevPersona,
  type ScenarioCatalog,
  type ScenarioGrant,
  type ScenarioRole,
  type startDevPersona,
} from '../../lib/api/devPersonas';

// ─────────────────────────────────────────────────────────────────────
// THE RBAC SCENARIO BUILDER — dev persona bar v2 (#1024). DEVELOPMENT ONLY.
//
// «Tournament director AND treasurer in one club, but only trainer in
// another»: pick organizations, pick any of the roles each one actually has
// (system roles and its own, from the database), apply it to an isolated dev
// identity and sign in as it. Every rule is the SERVER's — which roles belong
// where, that only reserved dev identities are written, that nothing is a
// platform role — and this file renders what it is told.
//
// THE UNION IS READ, NEVER COMPUTED. «Effective rights now» is `/me`'s
// per-organization capability union, from the server's own evaluator. What
// this file adds is ATTRIBUTION — which of the roles `/me` names holds each
// capability, from the role definitions the catalog carries — and it never
// adds a capability the server did not report.
// ─────────────────────────────────────────────────────────────────────

type Selection = Record<string, string[]>;

/** The selection the identity holds now, as the builder's state. */
export function selectionFrom(grants: readonly ScenarioGrant[]): Selection {
  const out: Selection = {};
  for (const g of grants) if (g.roleIds.length) out[g.organizationId] = [...g.roleIds].sort();
  return out;
}

/** The request body: organizations with at least one role, in a stable order. */
export function grantsFrom(selection: Selection): ScenarioGrant[] {
  return Object.entries(selection)
    .filter(([, roleIds]) => roleIds.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([organizationId, roleIds]) => ({ organizationId, roleIds: [...roleIds].sort() }));
}

/**
 * Which of the roles `/me` reports in an organization hold `capability` —
 * attribution only. Matched on the role KEY, which is what `/me` names; a role
 * the catalog does not know contributes nothing here rather than a guess.
 */
export function capabilityContributors(
  capability: string,
  roleKeys: readonly string[],
  roles: readonly ScenarioRole[],
): string[] {
  return roleKeys.filter((key) =>
    roles.find((r) => r.key === key)?.capabilities.includes(capability),
  );
}

export interface DevRbacScenarioProps {
  onClose: () => void;
  /** Adopt the session — the same hook the persona bar hands its sessions to. */
  onSession: (
    persona: DevPersona,
    session: Awaited<ReturnType<typeof startDevPersona>>,
  ) => void | Promise<void>;
  /** The signed-in account, so the builder opens on ITS slot when it is one. */
  accountEmail: string | null;
  /** Injectable for tests. */
  loadCatalog?: typeof fetchScenarioCatalog;
  start?: typeof startScenario;
  loadMe?: typeof fetchMe;
}

const ERROR_KEY = {
  role_not_in_organization: 'dev.rbac.error.role',
  unknown_organization: 'dev.rbac.error.organization',
  scenario_changed: 'dev.rbac.error.changed',
  not_a_dev_identity: 'dev.rbac.error.identity',
} as const;

export function DevRbacScenario({
  onClose,
  onSession,
  accountEmail,
  loadCatalog = fetchScenarioCatalog,
  start = startScenario,
  loadMe = fetchMe,
}: DevRbacScenarioProps) {
  const t = useT();
  const titleId = useId();
  const [catalog, setCatalog] = useState<ScenarioCatalog | null | 'loading'>('loading');
  const [me, setMe] = useState<MeResult | null>(null);
  const [slot, setSlot] = useState(1);
  const [selection, setSelection] = useState<Selection>({});
  const [player, setPlayer] = useState(false);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void loadCatalog().then((c) => {
      if (!live) return;
      setCatalog(c);
      if (!c) return;
      // Open on the signed-in identity's own slot, holding what it holds — an
      // empty form would revoke the current scenario on the first «apply».
      const email = (accountEmail ?? '').toLowerCase();
      const mine = c.identities.find((i) => i.email.toLowerCase() === email) ?? c.identities[0];
      if (mine) {
        setSlot(mine.slot);
        setSelection(selectionFrom(mine.grants));
        setPlayer(mine.player);
      }
    });
    void loadMe().then((r) => {
      if (live) setMe(r);
    });
    return () => {
      live = false;
    };
  }, [loadCatalog, loadMe, accountEmail]);

  const ready = catalog !== 'loading' && catalog !== null ? catalog : null;
  const orgName = (id: string) => ready?.organizations.find((o) => o.id === id)?.name ?? id;
  const rolesOf = (id: string) => ready?.organizations.find((o) => o.id === id)?.roles ?? [];
  const roleName = (orgId: string, key: string) =>
    rolesOf(orgId).find((r) => r.key === key)?.name ?? key;
  const kindLabel = (kind: string) =>
    kind === 'CENTRE' ? t('auth.scope.kind.centre') : t('auth.scope.kind.club');

  const chooseSlot = (next: number) => {
    setSlot(next);
    const identity = ready?.identities.find((i) => i.slot === next);
    setSelection(selectionFrom(identity?.grants ?? []));
    setPlayer(identity?.player ?? false);
    setError(null);
  };

  const toggle = (orgId: string, roleId: string) => {
    setSelection((prev) => {
      const held = new Set(prev[orgId] ?? []);
      if (held.has(roleId)) held.delete(roleId);
      else held.add(roleId);
      return { ...prev, [orgId]: [...held].sort() };
    });
  };

  const selectedCount = Object.values(selection).reduce((n, r) => n + r.length, 0);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = ready?.organizations ?? [];
    return q ? list.filter((o) => `${o.name} ${o.slug}`.toLowerCase().includes(q)) : list;
  }, [ready, filter]);

  const apply = async () => {
    const identity = ready?.identities.find((i) => i.slot === slot);
    if (!identity) return;
    setBusy(true);
    setError(null);
    try {
      const res = await start({ slot, grants: grantsFrom(selection), player });
      if (res.state === 'ok') {
        await onSession(
          {
            key: `scenario-${slot}`,
            label: identity.displayName,
            kind: 'club',
            email: identity.email,
          },
          { state: 'ok', session: res.session },
        );
        onClose();
        return;
      }
      setError(
        res.state === 'refused'
          ? t(ERROR_KEY[res.code as keyof typeof ERROR_KEY] ?? 'dev.rbac.error.generic')
          : t('dev.rbac.error.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  // ── EFFECTIVE RIGHTS NOW, as `/me` reports them ────────────────────
  const union = (() => {
    if (me === null) return <p className="text-[11px] text-ink-3">{t('dev.rbac.union.loading')}</p>;
    if (me.state !== 'ok') {
      return <p className="text-[11px] text-ink-3">{t('dev.rbac.union.unknown')}</p>;
    }
    const tenants = me.me.authorization.tenants;
    return (
      <>
        <p className="text-[11px] text-ink-2 mb-1">
          {t('dev.rbac.union.lead', {
            name: me.me.account.displayName ?? me.me.account.email ?? '',
          })}
        </p>
        {tenants.length === 0 ? (
          <p className="text-[11px] text-ink-3">{t('dev.rbac.union.none')}</p>
        ) : (
          <ul className="space-y-2" data-testid="dev-rbac-union">
            {tenants.map((tenant) => (
              <li key={tenant.tenantId} data-testid={`dev-rbac-union-${tenant.tenantId}`}>
                <div className="text-xs font-semibold text-ink-1">{orgName(tenant.tenantId)}</div>
                <div className="text-[11px] text-ink-2">
                  {t('dev.rbac.union.roles', {
                    roles: tenant.roles.map((k) => roleName(tenant.tenantId, k)).join(', '),
                  })}
                </div>
                <ul className="mt-1 flex flex-wrap gap-1">
                  {(tenant.capabilities ?? []).map((cap) => {
                    const from = capabilityContributors(
                      cap,
                      tenant.roles,
                      rolesOf(tenant.tenantId),
                    ).map((k) => roleName(tenant.tenantId, k));
                    return (
                      <li
                        key={cap}
                        className="rounded bg-bg-soft px-1.5 py-0.5 font-mono text-[10px] text-ink-1"
                        title={
                          from.length
                            ? t('dev.rbac.union.from', { roles: from.join(', ') })
                            : undefined
                        }
                      >
                        {cap}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="dev-rbac-scenario"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-line bg-white p-4 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-sm font-bold text-ink-1">
            {t('dev.rbac.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('dev.rbac.close')}
            className="rounded p-1 text-ink-2 hover:bg-bg-soft"
          >
            <X size={14} />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-ink-2">{t('dev.rbac.lead')}</p>

        <section className="mt-3 rounded-lg border border-line p-2">
          <h3 className="text-[10px] font-mono uppercase tracking-wide text-ink-3 mb-1">
            {t('dev.rbac.union.title')}
          </h3>
          {union}
        </section>

        {catalog === 'loading' ? (
          <p className="mt-3 text-[11px] text-ink-3">{t('dev.rbac.loading')}</p>
        ) : !ready ? (
          <p className="mt-3 text-[11px] text-ink-3">{t('dev.rbac.unavailable')}</p>
        ) : (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-[11px] text-ink-2">
                <span className="block">{t('dev.rbac.identity')}</span>
                <select
                  value={slot}
                  onChange={(e) => chooseSlot(Number(e.target.value))}
                  className="mt-0.5 rounded border border-line px-2 py-1 text-xs"
                  data-testid="dev-rbac-slot"
                >
                  {ready.identities.map((i) => (
                    <option key={i.slot} value={i.slot}>
                      {i.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-ink-2">
                <input
                  type="checkbox"
                  checked={player}
                  onChange={(e) => setPlayer(e.target.checked)}
                  data-testid="dev-rbac-player"
                />
                {t('dev.rbac.player')}
              </label>
              <label className="text-[11px] text-ink-2 grow">
                <span className="block">{t('dev.rbac.filter')}</span>
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="mt-0.5 w-full rounded border border-line px-2 py-1 text-xs"
                />
              </label>
            </div>

            <div className="space-y-2">
              {visible.map((org) => (
                <fieldset
                  key={org.id}
                  className="rounded-lg border border-line p-2"
                  data-testid={`dev-rbac-org-${org.id}`}
                >
                  <legend className="px-1 text-xs font-semibold text-ink-1">
                    {org.name}{' '}
                    <span className="font-normal text-ink-3">· {kindLabel(org.kind)}</span>
                  </legend>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {org.roles.map((r) => (
                      <label key={r.id} className="flex items-center gap-1 text-[11px] text-ink-1">
                        <input
                          type="checkbox"
                          checked={(selection[org.id] ?? []).includes(r.id)}
                          onChange={() => toggle(org.id, r.id)}
                          data-testid={`dev-rbac-role-${org.id}-${r.id}`}
                        />
                        {r.name}
                        {!r.isSystem && (
                          <span className="text-ink-3">({t('dev.rbac.ownRole')})</span>
                        )}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-2">
              <span className="text-[11px] text-ink-2" data-testid="dev-rbac-count">
                {t('dev.rbac.selected', { count: String(selectedCount) })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelection({})}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-bg-soft"
                >
                  {t('dev.rbac.clear')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void apply()}
                  className="rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                  data-testid="dev-rbac-apply"
                >
                  {busy ? t('dev.rbac.applying') : t('dev.rbac.apply')}
                </button>
              </div>
            </div>
            {error && (
              <p role="alert" className="text-[11px] text-rose" data-testid="dev-rbac-error">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
